import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    IconButton,
    Tooltip,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    Chip,
    Stack,
    Alert,
    Snackbar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import { format, addDays } from 'date-fns';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Done as DoneIcon } from '@mui/icons-material';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { Badge } from '@mui/material';
import { appointmentService } from '../../services/appointmentService';
import { spreadsService } from '../../services/spreadsService';
import { errorHandler } from '../../utils/errorHandler';
import { Appointment } from '../../types/appointment';
import { AppointmentDetailsDialog } from '../../components/admin/AppointmentDetailsDialog';

interface SpreadInfo {
    id: string;
    title: string;
    points: number;
}

const CalendarAdmin: React.FC = () => {
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedAppointments, setSelectedAppointments] = useState<Appointment[]>([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });
    const [spreads, setSpreads] = useState<Map<string, SpreadInfo>>(new Map());
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

    const loadSpreads = async () => {
        try {
            const spreadsData = await spreadsService.getAll();
            const spreadsMap = new Map<string, SpreadInfo>();
            spreadsData.forEach(spread => {
                spreadsMap.set(spread.id, {
                    id: spread.id,
                    title: spread.title,
                    points: spread.points || 0
                });
            });
            setSpreads(spreadsMap);
        } catch (error) {
            errorHandler.handleError(error, 'Error loading spreads');
        }
    };

    const loadAppointments = async () => {
        try {
            setLoading(true);
            const appointmentsRef = collection(db, 'appointments');

            let q = query(
                appointmentsRef,
                where('status', '==', 'confirmed'),
                orderBy('date', 'asc')
            );

            const querySnapshot = await getDocs(q);
            const appointmentData: Appointment[] = [];
            
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                appointmentData.push({
                    id: doc.id,
                    userId: data.userId || '',
                    userName: data.name || '',
                    userEmail: data.email || '',
                    userPhone: data.phone || '',
                    date: data.date || '',
                    time: data.time || '',
                    status: data.status || 'pending',
                    paymentType: data.paymentType || 'regular',
                    paymentStatus: data.paymentStatus || 'waiting_confirmation',
                    notes: data.notes || '',
                    receiptUrl: data.receiptUrl || '',
                    receiptFileId: data.receiptFileId || '',
                    spreadId: data.spreadId || '',
                    createdAt: data.createdAt,
                    updatedAt: data.updatedAt,
                    rejectionReason: data.rejectionReason || ''
                });
            });

            setAppointments(appointmentData);
        } catch (error) {
            errorHandler.handleError(error, 'Error loading appointments');
            showSnackbar('Randevular yüklenirken bir hata oluştu', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSpreads();
        loadAppointments();
    }, []);

    useEffect(() => {
        if (selectedDate) {
            const formattedSelectedDate = format(selectedDate, 'yyyy-MM-dd');
            const appointmentsForDate = appointments.filter(
                app => app.date === formattedSelectedDate
            ).sort((a, b) => {
                // HH:mm formatındaki saatleri karşılaştır
                const timeA = a.time.split(':').map(Number);
                const timeB = b.time.split(':').map(Number);
                
                // Önce saat, sonra dakika karşılaştırması yap
                if (timeA[0] !== timeB[0]) {
                    return timeA[0] - timeB[0];
                }
                return timeA[1] - timeB[1];
            });
            setSelectedAppointments(appointmentsForDate);
        } else {
            setSelectedAppointments([]);
        }
    }, [selectedDate, appointments]);

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date);
    };

    const handleComplete = async (appointmentId: string) => {
        try {
            await appointmentService.updateStatus(appointmentId, 'completed');
            await loadAppointments();
            setConfirmDialogOpen(false);
            setSelectedAppointmentId(null);
            showSnackbar('Randevu başarıyla tamamlandı', 'success');
        } catch (error) {
            errorHandler.handleError(error, 'Error completing appointment');
            showSnackbar('Randevu tamamlanırken bir hata oluştu', 'error');
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleViewDetails = (appointment: Appointment) => {
        setSelectedAppointment(appointment);
        setDetailsDialogOpen(true);
    };

    const getStatusChipColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' => {
        switch (status) {
            case 'pending': return 'warning';
            case 'confirmed': return 'info';
            case 'completed': return 'success';
            case 'rejected': return 'error';
            default: return 'default';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending': return 'Ödeme Bekliyor';
            case 'confirmed': return 'Onaylandı';
            case 'completed': return 'Tamamlandı';
            case 'rejected': return 'Reddedildi';
            default: return 'Bilinmiyor';
        }
    };

    const renderDay = (date: Date, selectedDates: Array<Date | null>, dayProps: PickersDayProps<Date>) => {
        const formattedDate = format(date, 'yyyy-MM-dd');
        const hasAppointment = appointments.some(app => app.date === formattedDate);

        return (
            <Badge
                key={date.toString()}
                overlap="circular"
                badgeContent={hasAppointment ? '•' : undefined}
                color="primary"
            >
                <PickersDay {...dayProps} />
            </Badge>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
            <Container maxWidth="xl">
                <Box sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h4" gutterBottom>
                        Randevu Takvimi
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Onaylanmış randevuları takvim üzerinde görüntüleyebilir ve yönetebilirsiniz.
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Box sx={{ width: '100%', typography: 'body1' }}>
                                <StaticDatePicker
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    slots={{
                                        day: (props) => renderDay(props.day, [], props)
                                    }}
                                    slotProps={{
                                        toolbar: { hidden: true }
                                    }}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                {selectedDate ? format(selectedDate, 'dd MMMM yyyy', { locale: tr }) : 'Seçili Randevular'}
                            </Typography>
                            {selectedAppointments.length === 0 ? (
                                <Typography color="text.secondary">
                                    {selectedDate ? 'Bu tarihte randevu bulunmuyor' : 'Lütfen bir tarih seçin'}
                                </Typography>
                            ) : (
                                selectedAppointments.map((appointment) => (
                                    <Box
                                        key={appointment.id}
                                        sx={{
                                            p: 2,
                                            mb: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            '&:last-child': {
                                                mb: 0
                                            },
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: 'action.hover'
                                            }
                                        }}
                                        onClick={() => handleViewDetails(appointment)}
                                    >
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                                {appointment.userName}
                                            </Typography>
                                            <Chip
                                                label={getStatusText(appointment.status)}
                                                color={getStatusChipColor(appointment.status)}
                                                size="small"
                                            />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Saat: {appointment.time}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Telefon: {appointment.userPhone}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                            <Tooltip title="Tamamlandı olarak işaretle">
                                                <IconButton
                                                    size="small"
                                                    color="success"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setSelectedAppointmentId(appointment.id);
                                                        setConfirmDialogOpen(true);
                                                    }}
                                                >
                                                    <DoneIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                ))
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                <Dialog
                    open={confirmDialogOpen}
                    onClose={() => setConfirmDialogOpen(false)}
                >
                    <DialogTitle>Randevuyu Tamamla</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Bu randevuyu tamamlandı olarak işaretlemek istediğinize emin misiniz?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialogOpen(false)}>İptal</Button>
                        <Button
                            onClick={() => selectedAppointmentId && handleComplete(selectedAppointmentId)}
                            color="success"
                        >
                            Tamamla
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    <Alert
                        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <AppointmentDetailsDialog
                    open={detailsDialogOpen}
                    onClose={() => setDetailsDialogOpen(false)}
                    appointment={selectedAppointment}
                    spreadInfo={selectedAppointment ? spreads.get(selectedAppointment.spreadId) : undefined}
                />
            </Container>
        </LocalizationProvider>
    );
};

export default CalendarAdmin;